import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

import api from 'api';
import { isEmpty } from 'utils/types';

import { selectors as authSelectors } from 'state/auth';
import routes from 'utils/routes';

import { constants as dateAndTimeConstants } from 'utils/dateAndTimes';

import {
  actions as orderActions,
  selectors as orderSelectors,
  actionsByType,
} from 'state/order';
import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';

import {
  actions as salesIntelligenceActions,
  types as salesIntelligenceActionTypes,
  selectors as salesIntelligenceSelectors,
} from 'state/salesIntelligence';

function* fetchForRecurringOrderSuggestion({ payload }) {
  try {
    const user = yield select(authSelectors.getUser);
    const { data: suggestions } = yield call(api.getRecurringOrderSuggestion, {
      shipperId: user.shipper_id,
      params: { ...payload },
    });
    if (!isEmpty(suggestions)) {
      yield put(
        salesIntelligenceActions.setRecurringOrderSuggestion(suggestions[0])
      );
      yield put(salesIntelligenceActions.openRecurringOrderSuggestionsModal());
    }
  } catch (error) {
    yield console.error(error);
  }
}

function* dismissRecurringOrderSuggestion({ payload }) {
  try {
    const user = yield select(authSelectors.getUser);
    const suggestion = yield select(
      salesIntelligenceSelectors.getRecurringOrderSuggestion
    );
    const {
      id: suggestionId,
      data: { address_from: addressFrom, address_to: addressTo },
    } = suggestion;

    yield call(api.dismissRecurringOrderSuggestion, {
      shipperId: user.shipper_id,
      suggestionId,
    });
    yield put(salesIntelligenceActions.setRecurringOrderSuggestion(null));
    yield put(
      salesIntelligenceActions.closeRecurringOrderSuggestionsDiscardReasonsModal()
    );

    const { sendEvent = true, reason = null } = payload;

    if (sendEvent) {
      yield put(
        analyticsActions.sendEvent(
          analyticsEvents.salesIntelligence.recurringOrderSuggestionDismissed({
            id: suggestionId,
            reason,
            addressFrom,
            addressTo,
            user,
          })
        )
      );
    }
  } catch (error) {
    yield console.error(error);
  }
}

function* handleSetRecurringOrderSuggestion() {
  try {
    yield put(orderActions.setRecurring({ isRecurring: true }));
    const typeOfOrder = yield select(orderSelectors.getTypeOfOrder);
    const [shipmentKey] = yield select(orderSelectors.getShipmentKeys);

    const suggestion = yield select(
      salesIntelligenceSelectors.getRecurringOrderSuggestion
    );
    const user = yield select(authSelectors.getUser);
    const {
      id: suggestionId,
      data: { address_from: addressFrom, address_to: addressTo },
    } = suggestion;

    yield put(
      actionsByType.shipments[typeOfOrder].changeAddress({
        stopType: dateAndTimeConstants.STOP_TYPES.pickup,
        shipmentKey,
        address: { ...addressFrom },
      })
    );
    yield put(
      actionsByType.shipments[typeOfOrder].changeAddress({
        stopType: dateAndTimeConstants.STOP_TYPES.dropoff,
        shipmentKey,
        address: { ...addressTo },
      })
    );

    yield put(push(routes.newOrder));

    yield put(salesIntelligenceActions.closeRecurringOrderSuggestionsModal());
    yield put(
      analyticsActions.sendEvent(
        analyticsEvents.salesIntelligence.recurringOrderSuggestionAccepted({
          id: suggestionId,
          addressFrom,
          addressTo,
          user,
        })
      )
    );
  } catch (error) {
    yield console.error(error);
  }
}

export default function* () {
  yield takeLatest(
    salesIntelligenceActionTypes.fetchForRecurringOrderSuggestion$,
    fetchForRecurringOrderSuggestion
  );
  yield takeLatest(
    salesIntelligenceActionTypes.dismissRecurringOrderSuggestion$,
    dismissRecurringOrderSuggestion
  );
  yield takeLatest(
    salesIntelligenceActionTypes.acceptRecurringOrderSuggestion$,
    handleSetRecurringOrderSuggestion
  );
}

export { fetchForRecurringOrderSuggestion, dismissRecurringOrderSuggestion };
